/**
 * Complexity telemetry — a per-assessment-phrase record of exactly what the
 * runtime did, laid out to line up with `ComplexityOracle.xlsx` so a session can
 * be replayed in the sheet and compared column-for-column.
 *
 * Why record rather than recompute at dump time: the model
 * (`computeTrajectory`) is already validated 33/33 against the oracle, so
 * re-deriving the bands from the final verdict sequence would trivially match
 * itself and prove nothing about the running app. This captures the band the
 * runtime *actually used to request each phrase* (at plan time) and the verdict
 * it *actually graded* (at completion). {@link ComplexityTelemetry.selfCheck}
 * then diffs those live values against a clean `computeTrajectory` of the same
 * verdicts — any mismatch is a runtime bug (PAD threading, ordering, rest
 * interleaving), not a model error.
 *
 * This is a testing/diagnostic aid, not production behaviour. Nothing here
 * affects phrase selection.
 */

import { computeTrajectory } from './complexityModel';
import { TierConfig, Verdict } from './complexityModel.types';

/** One assessment phrase, filled across plan time and grade time. */
export interface PhaseRecord {
  /** 1-based assessment index — the oracle's Phrase # (column A). */
  idx: number;
  /** Verdict earned at this phrase — the oracle's Status (column B). `null`
   *  until the phrase has been graded. */
  verdict: Verdict | null;
  /** Weighted error tally that produced the verdict (red 1.0 + yellow 0.5). */
  errorTotal: number | null;
  redNotes: number | null;
  yellowNotes: number | null;
  /** Position the runtime was at for this phrase — the recurrence's tracked
   *  scalar, and the oracle's column C. Captured at plan time.
   *
   *  Under the sheet's floor anchoring this equals `crMin`; under the shipping
   *  centre anchoring it is the band's midpoint. Column C of the sheet is this
   *  value, so this is what a live oracle comparison must diff. */
  position: number;
  /** Band floor the runtime used to request this phrase. Derived from
   *  `position` via the tier's anchoring. */
  crMin: number;
  /** Band ceiling — the oracle's CR Max (column D). */
  crMax: number;
  /** PAD-delayed driver: verdict[idx - pad], the verdict that moved this band. */
  driver: Verdict | undefined;
  /** The oracle's PLATEAU value (anti-jitter cooldown) for this row. */
  cooldown: number;
  /** Rest phrases injected before this assessment phrase. */
  restsBefore: number;
  /** Live accuracy % at grade time. `null` until graded. */
  accuracy: number | null;
}

/** A single field that diverged between the live run and the pure model. */
export interface SelfCheckMismatch {
  idx: number;
  field: 'position' | 'crMin' | 'crMax';
  live: number;
  model: number;
}

export class ComplexityTelemetry {
  private readonly records = new Map<number, PhaseRecord>();

  /** Record the band used to request an assessment phrase (plan time). */
  recordPlan(record: {
    idx: number;
    position: number;
    crMin: number;
    crMax: number;
    driver: Verdict | undefined;
    cooldown: number;
    restsBefore: number;
  }): void {
    if (this.records.has(record.idx)) return; // idempotent — first plan wins
    this.records.set(record.idx, {
      idx: record.idx,
      verdict: null,
      errorTotal: null,
      redNotes: null,
      yellowNotes: null,
      position: record.position,
      crMin: record.crMin,
      crMax: record.crMax,
      driver: record.driver,
      cooldown: record.cooldown,
      restsBefore: record.restsBefore,
      accuracy: null,
    });
  }

  /** Fill in the verdict once the phrase has been graded (completion time). */
  recordGrade(
    idx: number,
    grade: {
      verdict: Verdict;
      errorTotal: number;
      redNotes: number;
      yellowNotes: number;
      accuracy: number;
    },
  ): void {
    const record = this.records.get(idx);
    if (record === undefined || record.verdict !== null) return;
    record.verdict = grade.verdict;
    record.errorTotal = grade.errorTotal;
    record.redNotes = grade.redNotes;
    record.yellowNotes = grade.yellowNotes;
    record.accuracy = grade.accuracy;
  }

  /** All records in assessment order. */
  rows(): PhaseRecord[] {
    return Array.from(this.records.values()).sort((a, b) => a.idx - b.idx);
  }

  /** Just the graded verdict sequence, e.g. "GYRRG" — paste into the oracle's
   *  Status column (B). Ungraded trailing phrases are omitted. */
  verdictString(): string {
    return this.rows()
      .filter((r) => r.verdict !== null)
      .map((r) => r.verdict)
      .join('');
  }

  /**
   * Tab-separated table, header + one row per phrase, ready to paste into a
   * spreadsheet. Columns are ordered A/B/C/D-first to overlay the oracle.
   */
  toTsv(): string {
    const header = [
      'Phrase#',
      'Verdict',
      // Position is the oracle's column C. CR_Min/CR_Max follow so the sheet's
      // C/D still line up when the tier is floor-anchored, where C == Position.
      'Position',
      'CR_Min',
      'CR_Max',
      'ErrorTotal',
      'Red',
      'Yellow',
      'Driver',
      'Cooldown',
      'RestsBefore',
      'Accuracy',
    ].join('\t');
    const rows = this.rows().map((r) =>
      [
        r.idx,
        r.verdict ?? '',
        r.position,
        r.crMin,
        r.crMax,
        r.errorTotal ?? '',
        r.redNotes ?? '',
        r.yellowNotes ?? '',
        r.driver ?? '',
        r.cooldown,
        r.restsBefore,
        r.accuracy ?? '',
      ].join('\t'),
    );
    return [header, ...rows].join('\n');
  }

  /**
   * Diff the live-recorded bands against a clean `computeTrajectory` of the same
   * verdicts. An empty array means the runtime reproduced the model exactly
   * (hence the oracle). Any entry is a runtime bug worth chasing.
   */
  selfCheck(config: TierConfig): SelfCheckMismatch[] {
    const graded = this.rows().filter((r) => r.verdict !== null);
    const verdicts = graded.map((r) => r.verdict as Verdict);
    const trajectory = computeTrajectory(verdicts, config, verdicts.length);
    const mismatches: SelfCheckMismatch[] = [];
    graded.forEach((row, i) => {
      const model = trajectory[i];
      if (Math.abs(row.position - model.position) > 1e-9) {
        mismatches.push({
          idx: row.idx,
          field: 'position',
          live: row.position,
          model: model.position,
        });
      }
      if (Math.abs(row.crMin - model.crMin) > 1e-9) {
        mismatches.push({ idx: row.idx, field: 'crMin', live: row.crMin, model: model.crMin });
      }
      if (Math.abs(row.crMax - model.crMax) > 1e-9) {
        mismatches.push({ idx: row.idx, field: 'crMax', live: row.crMax, model: model.crMax });
      }
    });
    return mismatches;
  }

  reset(): void {
    this.records.clear();
  }
}

/**
 * The oracle's parameter cells (column I) for a given tier config — paste these
 * into the sheet so its recurrence matches the run.
 */
export function oracleParams(config: TierConfig): Record<string, number> {
  return {
    complexity_start: config.complexityStart,
    CVR: config.cvr,
    CAI: config.cai,
    CDI: config.cdi,
    PAD: config.pad,
    AIM: config.aim,
    GIM: config.gim,
    CAT: config.cat,
  };
}

/** Live source of the current session's telemetry + config. Returns undefined
 *  when no complexity session is active (flag off, between sessions). */
export type FlowbotSource = () =>
  | { telemetry: ComplexityTelemetry; config: TierConfig }
  | undefined;

/** The console API attached to `window.flowbot`. */
export interface FlowbotConsole {
  rows(): PhaseRecord[];
  table(): void;
  tsv(): string;
  verdicts(): string;
  params(): Record<string, number>;
  check(): SelfCheckMismatch[];
  copy(): Promise<void>;
}

/**
 * Install a `window.flowbot` console helper for oracle comparison. Idempotent —
 * always reads the *current* session via `source`, so it survives the
 * controller being rebuilt on each tier change. Testing aid only; safe to call
 * where there is no `window` (no-op).
 *
 *   flowbot.table()    // console.table of every graded phrase
 *   flowbot.tsv()      // paste-into-spreadsheet TSV (also logged)
 *   flowbot.verdicts() // "GYRRG…" for the oracle's Status column
 *   flowbot.params()   // parameter cells to match the oracle
 *   flowbot.check()    // diff live bands vs the pure model — [] means faithful
 *   flowbot.copy()     // copy the TSV to the clipboard
 */
export function installFlowbotConsole(source: FlowbotSource): void {
  if (typeof window === 'undefined') return;

  const active = (): { telemetry: ComplexityTelemetry; config: TierConfig } => {
    const current = source();
    if (current === undefined) {
      throw new Error('flowbot: no active complexity session (is the feature flag on?)');
    }
    return current;
  };

  const api: FlowbotConsole = {
    rows: () => active().telemetry.rows(),
    table: () => {
      console.table(active().telemetry.rows());
    },
    tsv: () => {
      const text = active().telemetry.toTsv();

      console.log(text);
      return text;
    },
    verdicts: () => active().telemetry.verdictString(),
    params: () => oracleParams(active().config),
    check: () => {
      const { telemetry, config } = active();
      const mismatches = telemetry.selfCheck(config);

      console.log(
        mismatches.length === 0 ? 'flowbot: live run matches the model exactly ✓' : mismatches,
      );
      return mismatches;
    },
    copy: async () => {
      await navigator.clipboard.writeText(active().telemetry.toTsv());

      console.log('flowbot: TSV copied to clipboard');
    },
  };

  (window as unknown as { flowbot: FlowbotConsole }).flowbot = api;
}
